import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Send, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { Chapter, SectionTitle } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", organization: "", interest: "investment", message: "" });
  const [status, setStatus] = useState("idle");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setStatus("sending");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("success");
      toast.success(t("contact.success"));
      setForm({ name: "", email: "", organization: "", interest: "investment", message: "" });
      setTimeout(() => setStatus("idle"), 4000);
    } catch (err) {
      setStatus("idle");
      toast.error(t("contact.error"));
    }
  };

  const interests = ["investment", "partnership", "technical", "press", "general"];

  return (
    <section id="contact" className="py-24 sm:py-32 bg-white noise-bg">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Chapter>{t("contact.chapter")}</Chapter>
        <SectionTitle>{t("contact.title")}</SectionTitle>
        <div className="mt-14 grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-2 space-y-4">
            <a href="mailto:contact@horizon-affaires-senegal.com" data-testid="contact-email" className="flex items-center gap-4 bg-slate-50 border border-slate-200 rounded-xl p-5 hover:border-sky-300 transition-colors duration-200">
              <div className="w-11 h-11 rounded-xl bg-sky-100 flex items-center justify-center"><Mail className="w-5 h-5 text-sky-700" /></div>
              <div>
                <div className="text-xs text-slate-500">Email</div>
                <div className="text-slate-900 text-sm">contact@horizon-affaires-senegal.com</div>
              </div>
            </a>
            <a href="tel:+393667288052" data-testid="contact-phone" className="flex items-center gap-4 bg-slate-50 border border-slate-200 rounded-xl p-5 hover:border-sky-300 transition-colors duration-200">
              <div className="w-11 h-11 rounded-xl bg-green-100 flex items-center justify-center"><Phone className="w-5 h-5 text-green-700" /></div>
              <div>
                <div className="text-xs text-slate-500">Tél.</div>
                <div className="text-slate-900 text-sm">+39 366 728 8052</div>
              </div>
            </a>
            <div className="flex items-center gap-4 bg-slate-50 border border-slate-200 rounded-xl p-5">
              <div className="w-11 h-11 rounded-xl bg-slate-200 flex items-center justify-center"><MapPin className="w-5 h-5 text-slate-700" /></div>
              <div>
                <div className="text-xs text-slate-500">{t("contact.office")}</div>
                <div className="text-slate-900 text-sm">{t("contact.office_v")}</div>
              </div>
            </div>
          </div>

          <form onSubmit={submit} className="lg:col-span-3 bg-slate-50 border border-slate-200 rounded-2xl p-6 sm:p-8 space-y-5" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label className="text-slate-700">{t("contact.name")}</Label>
                <Input value={form.name} onChange={set("name")} placeholder={t("contact.ph_name")} required data-testid="input-name" className="mt-2 bg-white" />
              </div>
              <div>
                <Label className="text-slate-700">{t("contact.email")}</Label>
                <Input type="email" value={form.email} onChange={set("email")} placeholder={t("contact.ph_email")} required data-testid="input-email" className="mt-2 bg-white" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label className="text-slate-700">{t("contact.org")}</Label>
                <Input value={form.organization} onChange={set("organization")} placeholder={t("contact.ph_org")} data-testid="input-org" className="mt-2 bg-white" />
              </div>
              <div>
                <Label className="text-slate-700">{t("contact.interest")}</Label>
                <Select value={form.interest} onValueChange={(v) => setForm((f) => ({ ...f, interest: v }))}>
                  <SelectTrigger className="mt-2 bg-white" data-testid="select-interest"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {interests.map((i) => <SelectItem key={i} value={i} data-testid={`interest-${i}`}>{t(`contact.i_${i}`)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-slate-700">{t("contact.message")}</Label>
              <Textarea value={form.message} onChange={set("message")} placeholder={t("contact.ph_message")} required rows={5} data-testid="input-message" className="mt-2 bg-white resize-none" />
            </div>
            <motion.button
              whileTap={{ scale: 0.97 }}
              type="submit"
              disabled={status === "sending"}
              data-testid="contact-submit"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-sky-800 text-white px-8 py-3.5 font-medium hover:bg-sky-700 disabled:opacity-60 transition-colors duration-200"
            >
              {status === "sending" ? <><Loader2 className="w-4 h-4 animate-spin" />{t("contact.sending")}</>
                : status === "success" ? <><CheckCircle2 className="w-4 h-4" />{t("contact.success")}</>
                : <><Send className="w-4 h-4" />{t("contact.send")}</>}
            </motion.button>
          </form>
        </div>
      </div>
    </section>
  );
}
